<template>
  <div class="tag-cloud" ref="wrapper" :style="cloudStyle">
    <p v-for="(item, index) in data" :key="index" ref="tag" @click="clickTag(item)">
      <span :style="getTextStyle">{{ item.name }}</span>
    </p>
  </div>
</template>

<script>
export default {
  name: "tagCloud",
  props: {
    data: {
      type: Array,
      default: () => []
    },
    config: {
      type: Object,
      default: null
    }
  },
  computed: {
    getTextStyle() {
      return {
        "writing-mode":
          this.config.hasOwnProperty("textDirection") && this.config.textDirection === "vertical" ? "vertical-lr" : "",
        "text-decoration": this.config.textDecoration
      }
    },
    cloudStyle() {
      return {
        fontSize: this.option.maxFont + "px"
      }
    }
  },
  data() {
    return {
      option: {
        radius: 120, // 滚动半径，单位px
        maxFont: 24, // 最大字体大小
        color: null, // 字体颜色。为空时随机
        rotateAngleXbase: 600, // 默认旋转速度基数，数越小速度越快
        rotateAngleYbase: 600,
        hover: true, // 是否开启悬浮联动
        isAnimate: true // 是否翻转动画
      },
      tagList: [],
      isIntersecting: false
    }
  },
  created() {
    if (this.config != null) {
      this.option = Object.assign({}, this.option, this.config)
    }
  },
  mounted() {
    this._initTags()
    this.io = new IntersectionObserver(this.observeItem)
    this.io.observe(this.$refs.wrapper)
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    this.io.disconnect()
  },
  watch: {
    config: {
      handler(newName, oldName) {
        this.option = Object.assign({}, this.option, this.config)
        this.$nextTick(() => {
          this._initTags()
        })
      },
      deep: true
    },
    data() {
      this.$nextTick(() => {
        this._initTags()
      })
    }
  },
  methods: {
    _initTags() {
      clearInterval(this.timer)
      this.timer = null
      this.rotateAngleX = Math.PI / this.option.rotateAngleXbase
      this.rotateAngleY = this.rotateAngleX * Math.tan(parseInt(this.option.animateRotate))
      for (var i = 0, length = this.data.length; i < length; i++) {
        // 获取球面上均匀的点的经纬度 θ = arccos( ((2*num)-1)/all - 1); Φ = θ*sqrt(all * π);
        let angleX = Math.acos((2 * (i + 1) - 1) / length - 1)
        let angleY = angleX * Math.sqrt(length * Math.PI)
        // 根据经纬度获取点的坐标，球中心的点坐标是 (0,0,0) x=r*sinθ*cosΦ   y=r*sinθ*sinΦ   z=r*cosθ;
        let x,y,z
        if(this.option.wrapperWidth){
           x = this.option.wrapperWidth * Math.sin(angleX) * Math.cos(angleY)
           y = this.option.wrapperHeight * Math.sin(angleX) * Math.sin(angleY)
           z = this.option.radius * Math.cos(angleX)
        }else{
           x = this.option.radius * Math.sin(angleX) * Math.cos(angleY)
           y = this.option.radius * Math.sin(angleX) * Math.sin(angleY)
           z = this.option.radius * Math.cos(angleX)
        }
        if (this.option.color) {
          if (typeof this.option.color === "string") {
            this.$refs.tag[i].style.color = this.option.color
          } else {
            let colorMultiples = this.data.length / this.option.color.length
            if (colorMultiples <= 1) {
              this.$refs.tag[i].style.color = this.$parseColor(this.option.color[i])
            } else {
              this.$refs.tag[i].style.color = this.$parseColor(this.option.color[(i + 1) % this.option.color.length])
            }
          }
        } else {
          // 随机颜色
          this.$refs.tag[i].style.color =
            "rgb(" +
            Math.round(255 * Math.random()) +
            "," +
            Math.round(255 * Math.random()) +
            "," +
            Math.round(255 * Math.random()) +
            ")"
        }
        // 每个标签对象都有四对值
        var tag = {
          x: x,
          y: y,
          z: z,
          ele: this.$refs.tag[i]
        }
        this.tagList.push(tag)
      }
      this.startAnimation()
    },
    setPosition(tag, r, maxFont, minFont) {
      // 设置每个标签的坐标位置和字体大小以及透明度
      if (this.$refs.wrapper) {
        const transX = tag.x + this.$refs.wrapper.offsetWidth / 2 - tag.ele.offsetWidth / 2
        const transY = tag.y + this.$refs.wrapper.offsetHeight / 2 - tag.ele.offsetHeight / 2
        const scale = (tag.z / r / 2 + 0.5) * maxFont < minFont ? minFont / maxFont : tag.z / r / 2 + 0.5
        tag.ele.style.transform = `translate(${transX}px, ${transY}px) scale(${scale})`
        tag.ele.style.opacity = tag.z / r / 2 + 0.7
      }
    },
    rotateX(tag) {
      var cos = Math.cos(this.rotateAngleX)
      var sin = Math.sin(this.rotateAngleX)
      var y1 = tag.y * cos - tag.z * sin
      var z1 = tag.y * sin + tag.z * cos
      tag.y = y1
      tag.z = z1
    },
    rotateY(tag) {
      var cos = Math.cos(this.rotateAngleY)
      var sin = Math.sin(this.rotateAngleY)
      var x1 = tag.z * sin + tag.x * cos
      var z1 = tag.z * cos - tag.x * sin
      tag.x = x1
      tag.z = z1
    },
    clickTag(item) {
      this.$emit("clickTag", item)
    },
    observeItem(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isIntersecting = true
          this.startAnimation()
        } else {
          this.isIntersecting = false
          if (this.timer) {
            clearInterval(this.timer)
            this.timer = null
          }
        }
      })
    },
    startAnimation() {
      if (!this.timer && this.isIntersecting) {
        const _self = this
        if (_self.option.isAnimate) {
          this.timer = setInterval(function () {
            for (var i = 0; i < _self.tagList.length; i++) {
              _self.rotateX(_self.tagList[i])
              _self.rotateY(_self.tagList[i])
              _self.setPosition(_self.tagList[i], _self.option.radius, _self.option.maxFont, _self.option.minFont)
            }
          }, 20)
        }
      }
    }
  }
}
</script>

<style scoped>
.tag-cloud {
  width: 80%;
  height: 100%;
  position: relative;
  color: #333;
  margin: 0 auto;
  text-align: center;
}

.tag-cloud p {
  position: absolute;
  top: 0;
  left: 0;
  color: #333;
  text-decoration: none;
  margin: 0 10px 15px 0;
  line-height: 18px;
  text-align: center;
  padding: 4px 9px;
  display: inline-block;
  border-radius: 3px;
}
</style>
